import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, Award, Globe2, Calendar, Tag, Store, ExternalLink, BadgeCheck } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { getSales } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

interface Sale {
  id: string;
  domain_name: string;
  price: string;
  platform: string;
  sale_type: string | null;
  date?: string | null;
  source?: string | null;
  source_url?: string | null;
  logo_url?: string | null;
}

const formatPrice = (price: string) => {
  if (!price || price === "—") return price;
  const num = Number(String(price).replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(num) || num === 0) return price;
  if (/^\$|USD/i.test(price)) return price;
  return `$${num.toLocaleString("en-US")}`;
};

const hasDisplayablePrice = (price?: string | null) => {
  const raw = String(price ?? "").trim();
  if (!raw || raw === "—" || raw === "-") return false;
  return priceNum(raw) > 0;
};

const priceNum = (p: string) => Number(String(p ?? "").replace(/[^0-9.]/g, "")) || 0;

const formatDate = (d?: string | null) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return d;
  }
};

interface Props {
  variant?: "mine" | "market";
}

const SalesTicker = ({ variant = "market" }: Props) => {
  const [sales, setSales] = useState<Sale[]>([]);
  const [selected, setSelected] = useState<Sale | null>(null);

  useEffect(() => {
    (async () => {
      const data = await getSales().catch(() => []);
      // Tickers are price-driven social proof: only show records with a real numeric price.
      const all = ((data ?? []) as Sale[]).filter((s) => hasDisplayablePrice(s.price));

      if (variant === "mine") {
        const list = all
          .filter((s) => s.sale_type === "mine")
          .sort((a, b) => priceNum(b.price) - priceNum(a.price))
          .slice(0, 50);
        setSales(list);
      } else {
        const list = all.filter((s) => s.sale_type !== "mine").slice(0, 40);
        setSales(list);
      }
    })();
  }, [variant]);

  const isMineVariant = variant === "mine";
  const placeholder: Sale[] = [
    {
      id: "p1",
      domain_name: isMineVariant ? "Add your first sale" : "No market sales yet",
      price: "—",
      platform: "Admin → Sales",
      sale_type: isMineVariant ? "mine" : null,
    },
  ];
  const list = sales.length > 0 ? sales : placeholder;
  const items = [...list, ...list, ...list];

  const labelText = isMineVariant ? "Our Sales" : "Market Sales";
  const LabelIcon = isMineVariant ? Award : TrendingUp;

  const selectedIsMine = selected?.sale_type === "mine";

  return (
    <>
      <div className="group block w-full overflow-hidden border-b border-border/60 bg-secondary/40 backdrop-blur first:border-t">
        <div className="flex items-center">
          <Link
            to="/sales"
            className={`z-10 flex shrink-0 items-center gap-2 border-r border-border/60 px-4 py-2 transition-opacity hover:opacity-90 ${
              isMineVariant
                ? "bg-emerald-500 text-background"
                : "bg-foreground text-background"
            }`}
          >
            <LabelIcon className="h-3.5 w-3.5" />
            <span className="text-[11px] font-semibold uppercase tracking-wider">{labelText}</span>
          </Link>
          <div className="relative flex-1 overflow-hidden">
            <div className="ticker-track flex items-center gap-3 whitespace-nowrap py-2 group-hover:[animation-play-state:paused]">
              {items.map((s, i) => {
                const isMine = s.sale_type === "mine";
                const isPlaceholder = s.id === "p1";
                return (
                  <button
                    type="button"
                    key={`${s.id}-${i}`}
                    onClick={() => !isPlaceholder && setSelected(s)}
                    disabled={isPlaceholder}
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs transition-all ${
                      isMine
                        ? "border-emerald-500/40 bg-emerald-500/10 hover:border-emerald-500 hover:bg-emerald-500/20"
                        : "border-border/60 bg-background/40 hover:border-foreground/40 hover:bg-background/80"
                    } ${isPlaceholder ? "cursor-default opacity-70" : "cursor-pointer hover:scale-[1.03]"}`}
                  >
                    {isMine ? (
                      <Award className="h-3 w-3 text-emerald-500" />
                    ) : (
                      <Globe2 className="h-3 w-3 text-muted-foreground" />
                    )}
                    <span className="font-display font-semibold text-foreground">{s.domain_name}</span>
                    <span className={`font-bold ${isMine ? "text-emerald-500" : "text-foreground/80"}`}>
                      {formatPrice(s.price)}
                    </span>
                    {s.platform && s.platform.toLowerCase() !== "unknown" && (
                      <span className="text-muted-foreground">· {s.platform}</span>
                    )}
                    <span
                      className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${
                        isMine
                          ? "bg-emerald-500 text-background"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {isMine ? "Ours" : "Market"}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-md overflow-hidden p-0">
          {selected && (
            <>
              {/* Header band */}
              <div
                className={`relative px-6 pb-5 pt-6 ${
                  selectedIsMine
                    ? "bg-gradient-to-br from-emerald-500/15 via-emerald-500/5 to-transparent"
                    : "bg-gradient-to-br from-foreground/10 via-foreground/5 to-transparent"
                }`}
              >
                <div className="mb-3 flex items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                      selectedIsMine
                        ? "bg-emerald-500 text-background"
                        : "bg-foreground text-background"
                    }`}
                  >
                    {selectedIsMine ? <BadgeCheck className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}
                    {selectedIsMine ? "Our Sale" : "Market Sale"}
                  </span>
                </div>

                <DialogHeader className="space-y-1 text-left">
                  <DialogTitle className="font-display text-2xl font-bold tracking-tight">
                    {selected.domain_name || "—"}
                  </DialogTitle>
                  <DialogDescription className="text-xs text-muted-foreground">
                    Verified transaction record
                  </DialogDescription>
                </DialogHeader>

                <div className="mt-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Sale Price
                  </p>
                  <p
                    className={`font-display text-4xl font-extrabold tracking-tight ${
                      selectedIsMine ? "text-emerald-500" : "text-foreground"
                    }`}
                  >
                    {formatPrice(selected.price) || "—"}
                  </p>
                </div>
              </div>

              {/* Details */}
              <div className="divide-y divide-border border-t border-border bg-card">
                <DetailRow icon={<Store className="h-4 w-4" />} label="Platform" value={selected.platform || "—"} />
                <DetailRow icon={<Calendar className="h-4 w-4" />} label="Date" value={formatDate(selected.date)} />
                {selected.source && (
                  <DetailRow icon={<Tag className="h-4 w-4" />} label="Source" value={selected.source} />
                )}
                {selected.source_url && (
                  <a
                    href={selected.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between gap-3 px-6 py-3 text-sm transition-colors hover:bg-muted/50"
                  >
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <ExternalLink className="h-4 w-4" />
                      View original listing
                    </span>
                    <span className="text-xs font-medium text-primary">Open →</span>
                  </a>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const DetailRow = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) => (
  <div className="flex items-center justify-between gap-3 px-6 py-3 text-sm">
    <span className="flex items-center gap-2 text-muted-foreground">
      {icon}
      {label}
    </span>
    <span className="font-medium text-foreground">{value}</span>
  </div>
);

export default SalesTicker;
